import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Home, Star, Share2, Heart, ShoppingCart } from 'lucide-react';

const Button = ({ children, variant = 'primary', size = 'md', className = '', ...props }) => {
  const baseStyles = 'rounded-lg font-semibold transition-all duration-300 flex items-center gap-2';
  
  const variantStyles = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm',
    secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5',
    lg: 'px-6 py-3 text-lg'
  };

  return (
    <button
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const ProductDetails = (products) => {
  let { id } = useParams();
  let product_details = products.product.filter((arr) => arr.id == id)[0];
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const renderContent = (content) => {
    switch (content.type) {
      case 'paragraph':
        return (
          <p className={`mb-4 ${content.style === 'bold' ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
            {content.text}
          </p>
        );
      case 'list':
        return (
          <ul className="ml-6 mb-4 space-y-2">
            {content.items.map((item, idx) => (
              <li key={idx} className="text-gray-600 list-disc">{item}</li>
            ))}
          </ul>
        );
      case 'heading':
        const HeadingTag = `h${content.level}`;
        return (
          <HeadingTag className="font-bold text-gray-800 mb-4">
            {content.text}
          </HeadingTag>
        );
      default:
        return null;
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product_details.image.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product_details.image.length) % product_details.image.length);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-1 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-700 flex items-center">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/products" className="hover:text-gray-700">Products</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">{product_details.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Image Section */}
        <div className="space-y-6">
          <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden bg-gray-100">
            <div className="relative h-[500px] w-full">
              <img 
                src={require(`../assets/img${product_details.image[currentImageIndex]}`)}
                alt={`${product_details.name} view ${currentImageIndex + 1}`}
                className="w-full h-full object-contain"
              />
              {/* Image Navigation */}
              <div className="absolute inset-0 flex items-center justify-between px-4">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={prevImage}
                  className="bg-white/80 hover:bg-white"
                >
                  ←
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={nextImage}
                  className="bg-white/80 hover:bg-white"
                >
                  →
                </Button>
              </div>
            </div>
          </div>

          {/* Thumbnail Strip */}
          <div className="flex space-x-4 overflow-x-auto pb-2">
            {product_details.image.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`
                  flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden 
                  ${idx === currentImageIndex 
                    ? 'ring-2 ring-blue-500' 
                    : 'ring-1 ring-gray-200 hover:ring-gray-300'}
                `}
              >
                <img 
                  src={require(`../assets/img${img}`)}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Product Information */}
        <div className="space-y-8">
          {/* Header */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-gray-900 leading-tight">
              {product_details.name}
            </h1>
            
            {/* Action Buttons */}
            {/* <div className="flex gap-4">
              <Button variant="outline" onClick={() => setIsWishlisted(!isWishlisted)}>
                <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-blue-600' : ''}`} />
                Wishlist
              </Button>
              <Button variant="outline">
                <Share2 className="w-5 h-5" />
                Share
              </Button>
            </div> */}
          </div>
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Interested in this product?</h3>
            <div className="flex gap-4">
              <Link to={"/contact"} state={{selectedProduct:{id:product_details.id,name:product_details.name}}}>
              <Button size="lg" className="flex-1">
                <ShoppingCart className="w-5 h-5" />
                Request Quote
              </Button>
              </Link>
              {/* <Button variant="outline" size="lg" className="flex-1">
                Contact Sales
              </Button> */}
            </div>
          </div>
          {/* Description Card */}
          <div className="bg-white rounded-2xl p-6 space-y-4 shadow-sm ring-1 ring-gray-100">
            {/* <div className="prose max-w-none">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Product Description</h2>
              <p className="text-gray-600 leading-relaxed">
                {product_details.description}
              </p>
            </div> */}

            {/* Details Section */}
            {product_details['content']?.length > 0 && (
              <div className="border-t border-gray-100 pt-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Features</h3>
                <div className="prose max-w-none text-gray-600">
                  {product_details['content'].map((element, index) => 
                    renderContent(element, index)
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Contact Section */}
          
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;